import React from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useHomeStrings } from '../../../i18n';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function CodeSamples() {
  const t = useHomeStrings().codeSamples;
  const categories = [
    {
      id: 1,
      label: 'JavaScript',
      language: 'javascript',
      code: `import fs from 'fs';
import path from 'path';

async function compressGLB(inputPath, outputPath) {
  const fileBuffer = fs.readFileSync(inputPath);
  const formData = new FormData();
  formData.append('file', new Blob([fileBuffer]), path.basename(inputPath));

  const upload = await fetch('https://api.compress-glb.com/api/1/files/upload/sync', {
    method: 'POST',
    headers: { 'Authorization': '<API_KEY>' },
    body: formData
  });
  const [job] = await upload.json();

  const compressed = await fetch(job.compressed_file_url);
  fs.writeFileSync(outputPath, Buffer.from(await compressed.arrayBuffer()));
}`,
    },
    {
      id: 2,
      label: 'Python',
      language: 'python',
      code: `import requests

def compress_gltf(input_path, output_path):
    with open(input_path, 'rb') as f:
        r = requests.post('https://api.compress-glb.com/api/1/files/upload/sync',
                          files={'file': f},
                          headers={'Authorization': '<API_KEY>'})
        job = r.json()[0]

    cr = requests.get(job['compressed_file_url'])
    with open(output_path, 'wb') as out:
        out.write(cr.content)`,
    },
    {
      id: 3,
      label: 'Command Line',
      language: 'bash',
      code: `curl -X POST "https://api.compress-glb.com/api/1/files/upload/sync" \\
  -H "Authorization: <API_KEY>" \\
  -F "file=@input.glb" \\
  -o job.json

COMPRESSED_URL=$(jq -r '.[0].compressed_file_url' job.json)
curl -H "Authorization: <API_KEY>" "$COMPRESSED_URL" --output compressed-model.glb
`,
    },
  ];

  return (
    <div className="bg-background" id="code-samples">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center mb-12">
          <h2 className="text-base text-accent font-semibold tracking-wide uppercase">
            {t.eyebrow}
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-foreground sm:text-4xl">
            {t.heading}
          </p>
          <p className="mt-4 max-w-2xl text-xl text-muted-foreground/90 lg:mx-auto">
            {t.lead}
          </p>
        </div>
        <div className="w-full max-w-3xl mx-auto">
          <TabGroup>
            <TabList className="flex p-1 space-x-1 bg-background-elevated rounded-xl border border-border/60">
              {categories.map((cat) => (
                <Tab
                  key={cat.id}
                  className={({ selected }) =>
                    classNames(
                      'w-full py-2.5 text-sm leading-5 font-medium rounded-lg',
                      'focus:outline-none focus:ring-2 ring-offset-2 ring-offset-background ring-accent/50',
                      selected ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-background-soft hover:text-foreground transition-all duration-200'
                    )
                  }
                >
                  {cat.label}
                </Tab>
              ))}
            </TabList>
            <TabPanels className="mt-4">
              {categories.map((cat) => (
                <TabPanel
                  key={cat.id}
                  className="bg-background-elevated rounded-xl border border-border/60 p-3 focus:outline-none focus:ring-2 ring-offset-2 ring-offset-background ring-accent/50 shadow-sm"
                >
                  <SyntaxHighlighter language={cat.language} style={dracula} className="text-sm">
                    {cat.code}
                  </SyntaxHighlighter>
                </TabPanel>
              ))}
            </TabPanels>
          </TabGroup>
        </div>
      </div>
    </div>
  );
}
